import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { JoiValidationOptionsConstant } from '@vivli/shared/infrastructure/constants';
import { IListingRequest, IListingRequester } from '@vivli/features/listing-request/infrastructure/interface';
import joi, { ObjectSchema } from 'joi';
import { IStudy, IStudyResearchTeamMember } from '@vivli/features/studies/infrastructure/interface';
import { orcidInputSchema } from '@vivli/shared/infrastructure/schema';

const requesterSchema: ObjectSchema<IListingRequester> = joi
    .object({
        institutionName: joi.string().required().label('Institution Name')
    })
    .options(JoiValidationOptionsConstant);

const studyResearchTeamMembersSchema: ObjectSchema<IStudyResearchTeamMember> = joi
    .object({
        emailAddress: joi
            .string()
            .email({ tlds: { allow: false } })
            .required()
            .label('Email Address'),
        givenName: joi.string().required().label('Given Name'),
        familyName: joi.string().required().label('Family Name'),
        orcidId: orcidInputSchema('ORCID iD'),
        creditRoles: joi.array().min(1).required().label('CRediT Role(s)')
    })
    .options(JoiValidationOptionsConstant);

const datasetsSchema: ObjectSchema<IStudy> = joi
    .object({
        embargoDate: joi.when(joi.ref('/isDataEmbargoed'), {
            is: true,
            then: joi.date().required().max(new Date('12/31/2099')).label('Embargo Date')
        }),

        nctId: joi
            .string()
            .regex(/^NCT\d{8}$/)
            .required()
            .when('sponsorProtocolId', {
                is: joi.string().required().min(1),
                then: joi.string().allow('')
            })
            .label('NCT ID')
            .messages({
                'string.pattern.base': '{{#label}} must be entered in the format NCT12345678.'
            }),
        sponsorProtocolId: joi.string().required().label('Sponsor Protocol Id'),
        funderEmail: joi.string().required().label('Contact for Invoicing'),

        studyTitle: joi.string().required().label('Study Title'),

        //DEV-907: Conditions and Interventions are string[] on IStudy, so "must have a value" is .min(1) rather than a
        //string check. JoiValidationOptionsConstant already maps array.min to the shared "{#label} is required."
        //message, so no messages block is needed here.
        //Delimiter note, recorded rather than reconciled: this form splits both fields on a COMMA
        //(listing-request-study-nonNct.component.tsx handleChange), while the non-NCT bulk upload CSV splits the same
        //two fields on a SEMICOLON (StudyBulkOperationService.Upload.cs). Changing either side would reinterpret data
        //already stored under the other convention.
        extractedConditions: joi.array().min(1).required().label('Conditions'),
        extractedInterventions: joi.array().min(1).required().label('Interventions'),

        //DEV-907: Phase is required on the manual (non-NCT) path only. ClinicalTrials.gov does not report a phase for
        //every trial - Extractor.GetPhase returns PhaseEnum.None, which serializes to '' - and the NCT path offers no
        //Phase control at all: it renders ListingRequestStudySampleComponent, which prints the value as plain text.
        //An NCT submitter therefore has no way to satisfy a global rule.
        //Branching on nctId because hasNonNctValue is component state and is not readable from here.
        //Use the root joi.when form, NOT a chained joi.string().required().when(): joi CONCATENATES the base schema
        //with the branch rather than replacing it, so a chained "then" could not relax the required() above it.
        phase: joi
            .when('nctId', {
                is: joi.string().required().min(1),
                then: joi.any(),
                otherwise: joi.string().required()
            })
            .label('Phase'),

        researchTeam: joi.array().items(studyResearchTeamMembersSchema)
    })
    .options(JoiValidationOptionsConstant);

const formSchema: ObjectSchema<IListingRequest> = joi
    .object({
        requester: requesterSchema,
        numberOfIntendedStudies: joi.number().min(1).required().label('Intended Studies'),
        datasets: joi.array().items(datasetsSchema)
    })
    .options(JoiValidationOptionsConstant);

const defaultValues = (listingRequest) => ({
    ...listingRequest,
    datasetIds: listingRequest?.datasetIds || [],
    requester: listingRequest?.requester || {
        email: '',
        institutionName: '',
        userId: ''
    },
    numberOfIntendedStudies: listingRequest?.numberOfIntendedStudies || 1,
    // DEV-890: hasBeenSigned is obsolete but stays in the form so existing values round-trip on save instead of resetting to false.
    hasBeenSigned: listingRequest?.hasBeenSigned || false,
    history: listingRequest?.history || [],
    status: listingRequest?.status || null,
    vivliOwner: listingRequest?.vivliOwner || '',
    datasets: listingRequest?.datasets?.length > 0 ? listingRequest?.datasets : [defaultStudyValues(listingRequest?.datasets[0])],
    isDataEmbargoed: listingRequest?.isDataEmbargoed || false,
    canBeContacted: listingRequest?.canBeContacted || false,
    willNeedHelpAnonymizing: listingRequest?.willNeedHelpAnonymizing || false
});

const defaultStudyValues = (study): IStudy => ({
    ...study,
    id: study?.id || null,
    embargoDate: study?.embargoDate || null,
    nctId: study?.nctId || '',
    sponsorProtocolId: study?.sponsorProtocolId || '',
    extractedInterventions: study?.extractedInterventions || [],
    extractedConditions: study?.extractedConditions || [],
    funder: study?.funder || 'N/A',
    funderEmail: study?.funderEmail || '',
    grant: study?.grant || '',
    additionalInformation: study?.additionalInformation || '',
    orgName: study?.orgName || '',
    orgId: study?.orgId || '',
    orgCode: study?.orgCode || '',
    researchTeam: study?.researchTeam || undefined
});

export function useListingRequestFormHook(listingRequest?: IListingRequest, mode: 'onBlur' | 'onChange' | 'onTouched' | 'all' = 'all') {
    return useForm({
        mode,
        resolver: joiResolver(formSchema.options(JoiValidationOptionsConstant)),
        defaultValues: defaultValues(listingRequest),
        reValidateMode: 'onChange'
    });
}
